import { routeLegFor } from "./extraction-to-preview";

const DEFAULT_MAX = 15;

// Must match backend start-import.config: the school created for "I'll say which school later",
// and the label its auto-numbered services get ("Service 1", "Service 1 PM").
export const PLACEHOLDER_SCHOOL_NAME = "Default School";
export const GENERIC_SERVICE_LABEL = "Service";

export const clampCapacity = (value, fallback = DEFAULT_MAX) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(50, Math.max(4, Math.round(n)));
};

// One direction of the service draft. Mirrors backend planNamedAndClustered:
//  - a service named in the file is the operator's vehicle: created once with everyone who rides it,
//    carrying the file's own times/plate for that direction; over-full ones are flagged, not split
//  - passengers without a service name are grouped by school and capacity; afternoon groups get " PM"
export const draftDirection = (
  passengers = [],
  maxPerVehicle = DEFAULT_MAX,
  fallbackSchool = "Imported school",
  direction = "pickup",
  routeList = [],
  { autoLabel = "" } = {},
) => {
  const cap = clampCapacity(maxPerVehicle);
  // Auto-numbered groups are named after the school unless a generic label is asked for
  // (placeholder school → "Service 1", "Service 1 PM"); mirrors the backend planner.
  const baseFor = (school) => autoLabel || school;
  const field = direction === "dropoff" ? "dropoffRouteName" : "pickupRouteName";
  const named = new Map();
  const rest = new Map();
  passengers
    .filter((passenger) => passenger.name)
    .forEach((passenger) => {
      const school = passenger.schoolName || fallbackSchool;
      const label = String(passenger[field] || "").trim();
      const bucket = label ? named : rest;
      const key = label ? `${school}|${label}` : school;
      if (!bucket.has(key)) bucket.set(key, { school, label: label || school, list: [] });
      bucket.get(key).list.push(passenger);
    });

  const groups = [];
  named.forEach(({ school, label, list }) => {
    groups.push({
      id: `${direction}-${school}-${label}`,
      name: label,
      school,
      direction,
      named: true,
      leg: routeLegFor(routeList, label, direction),
      passengers: list,
      passengerCount: list.length,
      capacity: cap,
      overCapacity: list.length > cap,
    });
  });
  rest.forEach(({ school, list }) => {
    let index = 1;
    for (let i = 0; i < list.length; i += cap) {
      const slice = list.slice(i, i + cap);
      groups.push({
        id: `${direction}-${school}-auto-${index}`,
        name: `${baseFor(school)} ${index}${direction === "dropoff" ? " PM" : ""}`,
        school,
        direction,
        named: false,
        leg: null,
        passengers: slice,
        passengerCount: slice.length,
        capacity: cap,
        overCapacity: false,
      });
      index += 1;
    }
  });
  return groups;
};

export const draftServicePlan = (
  passengers = [],
  maxPerVehicle = DEFAULT_MAX,
  fallbackSchool = "Imported school",
  direction = "both",
  routeList = [],
  options = {},
) => ({
  pickup: direction === "dropoff" ? [] : draftDirection(passengers, maxPerVehicle, fallbackSchool, "pickup", routeList, options),
  dropoff: direction === "pickup" ? [] : draftDirection(passengers, maxPerVehicle, fallbackSchool, "dropoff", routeList, options),
});

// Kept for callers that only need one flat list (morning first).
export const groupPassengersByCapacity = (passengers = [], maxPerVehicle = DEFAULT_MAX, fallbackSchool = "Imported school") =>
  draftDirection(passengers, maxPerVehicle, fallbackSchool, "pickup");
